import React, { useState, useEffect, useContext, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import { MedicaidTestContext } from '../../contexts/MedicaidTestContext';

const isDevelopment = process.env.REACT_APP_MODE === 'development';

const VERDICT_COLORS = {
  pass: 'success',
  warn: 'warning',
  fail: 'error',
};

function VerdictChip({ verdict }) {
  if (!verdict) return <Chip label="—" size="small" variant="outlined" />;
  return (
    <Chip
      label={verdict}
      size="small"
      color={VERDICT_COLORS[verdict] || 'default'}
    />
  );
}

function formatSectionName(key) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

function SectionVerdictRow({ sectionKey, verdicts, rubricRow }) {
  const [open, setOpen] = useState(false);
  const hasRubric = verdicts.ai_rubric != null && rubricRow != null;

  return (
    <>
      <TableRow
        sx={hasRubric ? { '& > *': { borderBottom: 'unset' }, cursor: 'pointer' } : undefined}
        hover={hasRubric}
        onClick={hasRubric ? () => setOpen(prev => !prev) : undefined}
      >
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasRubric && (
              <IconButton size="small" sx={{ mr: 0.5, p: 0 }}>
                {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            )}
            {formatSectionName(sectionKey)}
          </Box>
        </TableCell>
        <TableCell align="center"><VerdictChip verdict={verdicts.verdict} /></TableCell>
        <TableCell align="center"><VerdictChip verdict={verdicts.jsd_verdict} /></TableCell>
        <TableCell align="center"><VerdictChip verdict={verdicts.cosine_verdict} /></TableCell>
        <TableCell align="center"><VerdictChip verdict={verdicts.embedding_verdict} /></TableCell>
        <TableCell align="center"><VerdictChip verdict={verdicts.ai_rubric} /></TableCell>
      </TableRow>
      {hasRubric && (
        <TableRow>
          <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 1.5, px: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  AI Rubric — Score: {rubricRow.ai_overall_score} ({rubricRow.ai_overall_verdict})
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                  {[
                    ['Semantic Similarity', rubricRow.semantic_similarity],
                    ['Factual Consistency', rubricRow.factual_consistency_contradiction_risk],
                    ['Unique Info', rubricRow.unique_info],
                    ['Missing Info', rubricRow.missing_info],
                  ].map(([label, val]) => (
                    <Chip
                      key={label}
                      label={`${label}: ${val}/5`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>

                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                  {rubricRow.ai_reason}
                </Typography>

                {rubricRow.improvement_actions?.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Suggested Improvements
                    </Typography>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                      {rubricRow.improvement_actions.map((action, i) => (
                        <li key={i}>
                          <Typography variant="body2">{action}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function ManageMedicaidTests() {
  const {
    scenarios,
    loading,
    error,
    runResults,
    permitted,
    setError,
    checkPermission,
    getScenarios,
    generateBaseline,
    runTests,
    setRunResults,
    updateScenario,
    deleteScenario,
  } = useContext(MedicaidTestContext);

  const [selectedState, setSelectedState] = useState('');
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [baselineRuns, setBaselineRuns] = useState(3);
  const [generating, setGenerating] = useState(false);
  const [running, setRunning] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    checkPermission();
  }, []);

  useEffect(() => {
    if (permitted) {
      getScenarios();
    }
  }, [permitted]);

  const availableStates = useMemo(() => {
    const states = [...new Set(scenarios.map(s => s.state).filter(Boolean))];
    return states.sort();
  }, [scenarios]);

  const filteredScenarios = useMemo(() => {
    if (!selectedState) return scenarios;
    return scenarios.filter(s => s.state === selectedState);
  }, [scenarios, selectedState]);

  // Keep selected scenario in sync with fresh scenario data
  useEffect(() => {
    if (selectedScenario) {
      const fresh = scenarios.find(s => s._id === selectedScenario._id);
      if (fresh) setSelectedScenario(fresh);
    }
  }, [scenarios]);

  const handleStateFilter = (e) => {
    setSelectedState(e.target.value);
    setSelectedScenario(null);
    setRunResults(null);
  };

  const handleSelectScenario = (scenario) => {
    setSelectedScenario(scenario);
    setRunResults(null);
    setEditingName(false);
  };

  const handleGenerateBaseline = async () => {
    if (!selectedScenario) return;
    setGenerating(true);
    const result = await generateBaseline(selectedScenario._id, baselineRuns);
    setGenerating(false);
    if (result.success) {
      setSuccessMessage(`Baseline generated with ${baselineRuns} runs`);
    }
  };

  const handleRunTest = async () => {
    if (!selectedScenario) return;
    setRunning(true);
    const result = await runTests([selectedScenario._id]);
    setRunning(false);
    if (result.success) {
      setSuccessMessage('Test run completed');
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage('');
    setError(null);
  };

  const handleToggleEnabled = async () => {
    if (!selectedScenario) return;
    const newEnabled = !selectedScenario.enabled;
    const result = await updateScenario(selectedScenario._id, { enabled: newEnabled });
    if (result.success) {
      setSelectedScenario(result.data);
      setSuccessMessage(`Scenario ${newEnabled ? 'enabled' : 'disabled'}`);
    }
  };

  const handleStartEditName = () => {
    setEditNameValue(selectedScenario.name);
    setEditingName(true);
  };

  const handleCancelEditName = () => {
    setEditingName(false);
    setEditNameValue('');
  };

  const handleSaveName = async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed || trimmed === selectedScenario.name) {
      setEditingName(false);
      return;
    }
    const result = await updateScenario(selectedScenario._id, { name: trimmed });
    if (result.success) {
      setSelectedScenario(result.data);
      setSuccessMessage('Name updated');
    }
    setEditingName(false);
  };

  const handleDeleteScenario = async () => {
    if (!selectedScenario) return;
    const result = await deleteScenario(selectedScenario._id);
    setDeleteDialogOpen(false);
    if (result.success) {
      setSelectedScenario(null);
      setRunResults(null);
      setSuccessMessage('Scenario deleted');
    }
  };

  const scenarioDisabled = selectedScenario?.enabled === false;

  if (permitted === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (permitted === false) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Not permitted — Medicaid Tests are only available in development mode with an authorized account.
        </Alert>
      </Box>
    );
  }

  const currentResult =
    runResults?.results?.find(r => r.scenario?.id === selectedScenario?._id) ?? null;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Medicaid Test Scenarios
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 160px)' }}>
        {/* Left Panel */}
        <Box
          sx={{
            width: '40%',
            minWidth: 280,
            display: 'flex',
            flexDirection: 'column',
            borderRight: 1,
            borderColor: 'divider',
            pr: 2,
          }}
        >
          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
            <InputLabel>Filter by State</InputLabel>
            <Select
              value={selectedState}
              label="Filter by State"
              onChange={handleStateFilter}
            >
              <MenuItem value="">
                <em>All States</em>
              </MenuItem>
              {availableStates.map(st => (
                <MenuItem key={st} value={st}>{st}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {loading && scenarios.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <List
              sx={{
                overflow: 'auto',
                flex: 1,
              }}
              dense
            >
              {filteredScenarios.map(scenario => {
                const runCount = scenario.latest_baseline_summary?.run_count;
                const isDisabled = scenario.enabled === false;
                return (
                  <ListItemButton
                    key={scenario._id}
                    selected={selectedScenario?._id === scenario._id}
                    onClick={() => handleSelectScenario(scenario)}
                    sx={{ borderRadius: 1, mb: 0.5, opacity: isDisabled ? 0.5 : 1 }}
                  >
                    <ListItemText
                      primary={scenario.name}
                      secondary={
                        <Box component="span" sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {isDisabled && (
                            <Chip label="disabled" size="small" color="default" />
                          )}
                          {scenario.tags?.map(tag => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                          <Chip
                            label={runCount > 0 ? `${runCount} runs` : 'No baseline'}
                            size="small"
                            color={runCount > 0 ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                );
              })}
              {filteredScenarios.length === 0 && !loading && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  No scenarios found.
                </Typography>
              )}
            </List>
          )}
        </Box>

        {/* Right Panel */}
        <Box sx={{ width: '60%', overflow: 'auto', pl: 2 }}>
          {!selectedScenario ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="body1" color="text.secondary">
                Select a scenario from the list to view details.
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Scenario Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {editingName ? (
                  <>
                    <TextField
                      size="small"
                      value={editNameValue}
                      onChange={e => setEditNameValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleSaveName();
                        if (e.key === 'Escape') handleCancelEditName();
                      }}
                      autoFocus
                      sx={{ flex: 1, maxWidth: 360 }}
                    />
                    <IconButton size="small" color="success" onClick={handleSaveName}>
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEditName}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ color: scenarioDisabled ? 'text.disabled' : undefined }}>
                      {selectedScenario.name}
                    </Typography>
                    <IconButton size="small" onClick={handleStartEditName}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </>
                )}

                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title={scenarioDisabled ? 'Enable scenario' : 'Disable scenario'}>
                    <Switch
                      checked={!scenarioDisabled}
                      onChange={handleToggleEnabled}
                      size="small"
                    />
                  </Tooltip>
                  <Tooltip title="Delete scenario">
                    <IconButton size="small" color="error" onClick={() => setDeleteDialogOpen(true)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {scenarioDisabled && (
                <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                  This scenario is disabled. Enable it to run tests or generate baselines.
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 2, alignItems: 'center' }}>
                <Chip label={selectedScenario.slug} size="small" variant="outlined" />
                <Chip label={selectedScenario.state || '—'} size="small" color="primary" variant="outlined" />
                {selectedScenario.tags?.map(tag => (
                  <Chip key={tag} label={tag} size="small" />
                ))}
                {selectedScenario.create_date && (
                  <Typography variant="caption" color="text.secondary">
                    Created {new Date(selectedScenario.create_date).toLocaleDateString()}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Baseline Section */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Baseline
              </Typography>
              <Box sx={{ mb: 2 }}>
                {selectedScenario.latest_baseline_summary?.run_count > 0 ? (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      <strong>Runs:</strong> {selectedScenario.latest_baseline_summary.run_count}
                      {' · '}
                      <strong>Algorithm:</strong> {selectedScenario.latest_baseline_summary.algorithm_version || '—'}
                      {selectedScenario.latest_baseline_summary.updated_at && (
                        <>
                          {' · '}
                          <strong>Updated:</strong>{' '}
                          {new Date(selectedScenario.latest_baseline_summary.updated_at).toLocaleString()}
                        </>
                      )}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No baseline generated yet.
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <InputLabel>Runs</InputLabel>
                    <Select
                      value={baselineRuns}
                      label="Runs"
                      onChange={e => setBaselineRuns(e.target.value)}
                    >
                      {[3, 4, 5].map(n => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    onClick={handleGenerateBaseline}
                    disabled={generating || loading || scenarioDisabled}
                    startIcon={generating ? <CircularProgress size={16} /> : null}
                  >
                    {generating ? 'Generating…' : 'Generate Baseline'}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Run Test Section */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Run Test
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleRunTest}
                  disabled={running || loading || scenarioDisabled}
                  startIcon={running ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {running ? 'Running…' : 'Run Test'}
                </Button>
              </Box>

              {/* Verdict Results */}
              {currentResult && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Results
                  </Typography>

                  {/* Status badge + summary */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Alert
                      severity={
                        currentResult.result_status === 'completed'
                          ? currentResult.is_failure
                            ? 'error'
                            : 'success'
                          : currentResult.result_status === 'skipped'
                            ? 'warning'
                            : 'error'
                      }
                      sx={{ py: 0 }}
                    >
                      <strong>{currentResult.result_status}</strong>
                      {currentResult.section_verdict_summary?.summary && (
                        <> — {currentResult.section_verdict_summary.summary}</>
                      )}
                    </Alert>
                  </Box>

                  {currentResult.result_status === 'skipped' && currentResult.reason && (
                    <Alert severity="info" sx={{ mb: 2 }}>{currentResult.reason}</Alert>
                  )}

                  {currentResult.result_status === 'error' && currentResult.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{currentResult.error}</Alert>
                  )}

                  {/* Section verdict table */}
                  {currentResult.verdict_details?.sections && (() => {
                    const rubricBySection = {};
                    if (currentResult.ai_rubric?.rows) {
                      for (const row of currentResult.ai_rubric.rows) {
                        rubricBySection[row.section_key] = row;
                      }
                    }
                    return (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Section</strong></TableCell>
                              <TableCell align="center"><strong>Verdict</strong></TableCell>
                              <TableCell align="center"><strong>JSD</strong></TableCell>
                              <TableCell align="center"><strong>Cosine</strong></TableCell>
                              <TableCell align="center"><strong>Embedding</strong></TableCell>
                              <TableCell align="center"><strong>AI Rubric</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(currentResult.verdict_details.sections).map(
                              ([sectionKey, verdicts]) => (
                                <SectionVerdictRow
                                  key={sectionKey}
                                  sectionKey={sectionKey}
                                  verdicts={verdicts}
                                  rubricRow={rubricBySection[sectionKey] || null}
                                />
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Scenario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{selectedScenario?.name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteScenario} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ManageMedicaidTests;
